import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeftLong } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
import { ADD_DEVICE_DRIPSET_SRC } from '@constants/images';
import EllipseIcon from '@components/SvgComponents/EllipseIcon';
import {
    addDeviceData,
    checkDeviceIdExists,
    checkHospitalDeviceIdExists,
    checkIndividualDeviceIdExists,
    fetchHospitalBlockData,
    individualAddDeviceData,
} from '@utils/deviceUtils';
import ErrorMessage from './Error/ErrorMessage';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { scan } from '@utils/authUtils';
import { HOSPITALS_TABLE } from '@constants/dynamoDBTableName';
import { DEVICE_PATH } from '@constants/routes';
import { FaCircleCheck } from 'react-icons/fa6';
import {
    DEVICE_NUMBER_MIN_LENGTH,
    DEVICE_NUMBER_MAX_LENGTH,
    DEVICE_NUMBER_TITLE,
    DEVICE_NUMBER_PATTERN,
    DEVICE_NAME_MIN_LENGTH,
    DEVICE_NAME_MAX_LENGTH,
    DEVICE_NAME_TITLE,
} from '@constants/form';
import Swal from 'sweetalert2';
import { generateUniqueTimestamp } from '@utils/sharedUtils';
import { HOSPITAL, INDIVIDUAL } from '@constants/constants';
import MyTreeView from './TreeView/TreeView';
import { useAuditLog } from '../contexts/AuditLogContext';
import { v4 as uuidv4 } from 'uuid';
import { setTreeViewSelectedValue } from '@store/reducers/userSlice';

const MySwal = Swal;

type TreeViewDataType = Record<string, any>[];

const CreateDevice: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [deviceName, setDeviceName] = useState('');
    const [deviceStatus, setDeviceStatus] = useState('approved');
    const [deviceUniqueId, setDeviceUniqueId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [treeViewData, setTreeViewData] = useState<TreeViewDataType>([]);
    const loggedInUserInfoUuid = useAppSelector(
        (state: any) => state.login.loggedInUserInfoUuid
    );
    const loggedInUserTenantID = useAppSelector(
        (state: any) => state.login.loggedInUserTenantID
    );
    const role = useAppSelector((state: any) => state.login.loggedInUserRole);
    const treeViewSelectedValue = useAppSelector(
        (state: any) => state.user.treeViewSelectedValue
    );
    const loggedInUserID = useAppSelector(
        (state: any) => state.login.loggedInUserID
    );
    const loggedInUserRole = useAppSelector(
        (state: any) => state.login.loggedInUserRole
    );

    const { addLog } = useAuditLog();

    const fetchData = async () => {
        try {
            const hospitalBlock =
                await fetchHospitalBlockData(loggedInUserTenantID);
            setTreeViewData(hospitalBlock); // Set the fetched data to the state
        } catch (error) {
            console.error('Error fetching hospital block data:', error);
        }
    };

    useEffect(() => {
        if (loggedInUserRole.toLowerCase() == HOSPITAL.toLowerCase()) {
            fetchData();
        }
    }, [loggedInUserRole, HOSPITAL]);

    const backArrow = () => {
        dispatch(setTreeViewSelectedValue(''));
        navigate(-1);
    };
    const handleDeviceUniqueIDChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        if (/^\d*$/.test(value)) {
            setDeviceUniqueId(value);
        }
    };
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            if (loggedInUserRole.toLowerCase() == HOSPITAL.toLowerCase()) {
                const res = await checkHospitalDeviceIdExists(
                    deviceUniqueId,
                    loggedInUserTenantID
                );
                // console.log('res', res);
                if (res == 'Device ID exists' || res == 'Invalid Device ID') {
                    setError(res);
                } else {
                    const hospital = await scan(
                        HOSPITALS_TABLE,
                        loggedInUserTenantID
                    );
                    if (treeViewSelectedValue == '') {
                        setError('Please select hospital block');
                    }
                    if (
                        hospital &&
                        hospital?.length != 0 &&
                        treeViewSelectedValue != ''
                    ) {
                        const hospitalData = hospital[0];
                        const id = uuidv4();
                        const data = {
                            role: role,
                            tenant_id: loggedInUserTenantID,
                            device_id: deviceUniqueId,
                            device_name: deviceName,
                            device_status: 'inactive',
                            status: deviceStatus,
                            hospital_id: hospitalData?.uuid?.S,
                            user_id: loggedInUserID,
                            date: generateUniqueTimestamp(),
                            deviceUuid: id,
                            hospital_block_id: treeViewSelectedValue,
                        };
                        await addDeviceData(data);

                        addLog(
                            'Device Created',
                            id,
                            'Devices',
                            loggedInUserID,
                            { 'Device ID': deviceUniqueId, 'Device Name': deviceName },
                            window.location.hostname
                        );

                        // Reset form state
                        setDeviceName('');
                        setDeviceStatus('approved');
                        setDeviceUniqueId('');
                        setError(null);

                        // Show success message
                        MySwal.fire({
                            position: 'top-end', // Position the toast in the top-right corner
                            icon: 'success',
                            title: 'Device added successfully',
                            showConfirmButton: false,
                            timer: 1500,
                            toast: true,
                            customClass: {
                                popup: 'text-green bg-light_green', // Add custom class to the popup
                            },
                        });
                        navigate(-1);
                    }
                }
            } else {
                const res = await checkIndividualDeviceIdExists(
                    deviceUniqueId,
                    loggedInUserTenantID
                );

                if (res == 'Device ID exists' || res == 'Invalid Device ID') {
                    setError(res);
                } else {
                    const id = uuidv4();
                    const data = {
                        role: role,
                        tenant_id: loggedInUserTenantID,
                        device_id: deviceUniqueId,
                        device_name: deviceName,
                        device_status: 'inactive',
                        status: deviceStatus,
                        hospital_id: '',
                        user_id: loggedInUserInfoUuid,
                        date: generateUniqueTimestamp(),
                        deviceUuid: id,
                        hospital_block_id: '',
                    };
                    await individualAddDeviceData(data);

                    addLog(
                        'Device Created',
                        id,
                        'Devices',
                        loggedInUserID,
                        { 'Device ID': deviceUniqueId, 'Device Name': deviceName },
                        window.location.hostname
                    );

                    // Reset form state
                    setDeviceName('');
                    setDeviceStatus('approved');
                    setDeviceUniqueId('');
                    setError(null);

                    // Show success message
                    MySwal.fire({
                        position: 'top-end', // Position the toast in the top-right corner
                        icon: 'success',
                        title: 'Device added successfully',
                        showConfirmButton: false,
                        timer: 1500,
                        toast: true,
                        customClass: {
                            popup: 'text-green bg-light_green', // Add custom class to the popup
                        },
                    });
                    navigate(-1);
                }
            }
        } catch (error: any) {
            console.error('Error checking device ID:', error);
            // throw new Error('Error checking device ID');
            let errorMessage;

            switch (error.name) {
                case 'ValidationException':
                    errorMessage =
                        'Validation error: Please check your request parameters.';
                    break;
                case 'ProvisionedThroughputExceededException':
                    errorMessage =
                        'Provisioned throughput exceeded: Please try again later.';
                    break;
                case 'ResourceNotFoundException':
                    errorMessage =
                        'Resource not found: Please check your table name.';
                    break;
                case 'InternalServerError':
                    errorMessage =
                        'Internal server error: Please try again later.';
                    break;
                case 'ServiceUnavailable':
                    errorMessage =
                        'Service unavailable: Please try again later.';
                    break;
                default:
                    errorMessage = `Unknown error: ${error.message || error}`;
            }
            setError(errorMessage);
            // return errorMessage;
        }
    };

    return (
        <div className="mx-auto">
            <div className="font-bold text-black text-base">
                <div className="flex items-center" onClick={backArrow}>
                    <FaArrowLeftLong className="cursor-pointer" />{' '}
                    <h2 className="font-semibold ml-2 cursor-pointer">
                        {t('management.device.back')}
                    </h2>
                </div>
            </div>
            <div className="cart relative mx-auto bg-white rounded-2xl p-1 w-3/6">
                <div className="relative w-[178px] h-[178px] mx-auto mb-2 flex items-center justify-center">
                    <EllipseIcon />
                    <img
                        className="absolute left-[89px] top-[86px] transform -translate-x-1/2 -translate-y-1/2 w-[130px] h-[130px] object-contain"
                        src={ADD_DEVICE_DRIPSET_SRC}
                        alt="DEVICE_DRIPSET"
                    />

                </div>
                <p className="flex flex-col text-primary font-semibold items-center justify-center text-center">
                    {t('management.device.text')}
                </p>

                {error && (
                    <div className="w-3/5 mx-auto">
                        <ErrorMessage message={error} />
                    </div>
                )}

                {/* {successMessage && (
                    <div className="text-green bg-light_green font-medium text-sm my-4 py-2 w-3/5 mx-auto flex items-center justify-center rounded">
                        <FaCircleCheck className="text-xl mr-3" />
                        {successMessage}
                    </div>
                )} */}

                <form className="w-4/5 mx-auto mt-3" onSubmit={handleSubmit}>
                    <div className="mx-auto">
                        <label
                            htmlFor="device_unique_id"
                            className="mb-2 text-sm font-medium text-light_grey"
                        >
                            {t('management.device.device_unique_id')}
                        </label>
                        <input
                            type="text"
                            name="device_unique_id"
                            id="device_unique_id"
                            className="form-control px-3 py-2 placeholder-gains_boro"
                            placeholder={t('management.device.placeholder')}
                            value={deviceUniqueId}
                            // onChange={(e) => setDeviceUniqueId(e.target.value)}
                            onChange={handleDeviceUniqueIDChange}
                            pattern={DEVICE_NUMBER_PATTERN}
                            title={DEVICE_NUMBER_TITLE}
                            minLength={DEVICE_NUMBER_MIN_LENGTH}
                            maxLength={DEVICE_NUMBER_MAX_LENGTH}
                            required
                        />
                    </div>
                    <div className="mx-auto mt-4">
                        <label
                            htmlFor="device_name"
                            className="mb-2 text-sm font-medium text-light_grey"
                        >
                            {t('management.device.device_name')}
                        </label>
                        <input
                            type="text"
                            name="device_name"
                            id="device_name"
                            className="form-control px-3 py-2 placeholder-gains_boro"
                            placeholder="Enter device name"
                            value={deviceName}
                            onChange={(e) => setDeviceName(e.target.value)}
                            minLength={DEVICE_NAME_MIN_LENGTH}
                            maxLength={DEVICE_NAME_MAX_LENGTH}
                            title={DEVICE_NAME_TITLE}
                            required
                        />
                    </div>
                    {loggedInUserRole.toLowerCase() ==
                        HOSPITAL.toLowerCase() && (
                        <div className="mx-auto mt-4">
                            <label
                                htmlFor="device_status"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('tree_view.title')}
                            </label>
                            <div className="tree-container_1">
                                <div className="checkbox-container">
                                    <MyTreeView
                                        data={treeViewData}
                                        isDisabled={false}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {/* <div className="mx-auto mt-4">
                        <label
                            htmlFor="device_status"
                            className="mb-2 text-sm font-medium text-light_grey"
                        >
                            Status
                        </label>
                        <select
                            name="device_status"
                            id="device_status"
                            className="w-full px-3 py-[0.65rem] border rounded !placeholder-gains_boro focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                            value={deviceStatus}
                            onChange={(e) => setDeviceStatus(e.target.value)}
                        >
                            <option value="approved">Approve</option>
                            <option value="pending">Pending</option>
                            <option value="blocked">Block</option>
                        </select>
                    </div> */}
                    <div className="pb-5 flex cursor-pointer align-items-center justify-content-center mx-auto mt-4">
                        <button
                            className="bg-primary text-lg text-white py-2  px-4 rounded-lg w-full"
                            type="submit"
                        >
                            {t('tree_view.submit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
export default CreateDevice;
